import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";

import PublicPageLayout from "./PublicPageLayout";
import { getPublicSiteSx } from "./publicSiteStyles";

const pillars = [
  {
    title: "Identity checks raise the entry bar",
    body:
      "Ouro uses verification to make it harder for throwaway accounts and repeat offenders to move through the system unnoticed.",
    icon: <VerifiedUserOutlinedIcon />,
  },
  {
    title: "Human review still matters",
    body:
      "Not every trust decision should be hidden behind automation. Review and moderation stay part of the process where judgment is needed.",
    icon: <AdminPanelSettingsOutlinedIcon />,
  },
  {
    title: "Platform policy is visible",
    body:
      "Rules around conduct, verification, and access should be understandable before a player invests time into the platform.",
    icon: <PolicyOutlinedIcon />,
  },
  {
    title: "Reports are accountable",
    body:
      "Ouro aims for a system where reports have consequences for accuracy, which helps protect honest players from noise and abuse.",
    icon: <GavelOutlinedIcon />,
  },
];

export default function TrustAndSafetyPage() {
  const theme = useTheme();
  const sx = React.useMemo(() => getPublicSiteSx(theme), [theme]);

  return (
    <PublicPageLayout
      eyebrow="Trust and safety"
      title="Trust is not an afterthought on Ouro."
      description="The platform is being built around the idea that cleaner matches come from better entry standards, better accountability, and more visible systems for handling bad behavior."
      mapBackground
    >
      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>The trust layer has four jobs.</Typography>
        </Box>
        <Box sx={sx.cardGrid}>
          {pillars.map((item) => (
            <Box key={item.title} sx={sx.card}>
              <Box sx={sx.cardIconWrap}>{item.icon}</Box>
              <Typography sx={sx.cardTitle}>{item.title}</Typography>
              <Typography sx={sx.cardBody}>{item.body}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>Trust, anti-cheat, and reputation work together.</Typography>
        </Box>
        <Box sx={{ ...sx.cardGrid, gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" } }}>
          <Box sx={sx.accentPanel}>
            <Typography sx={sx.cardTitle}>Why this matters for match quality</Typography>
            <Typography sx={sx.cardBody}>
              Trust and safety is not only about blocking bad behavior after it
              happens. It is also about shaping who gets through the door, how
              accountability is preserved over time, and how much confidence a
              player can have in the people around them.
            </Typography>
          </Box>
          <Box sx={sx.card}>
            <Typography sx={sx.cardTitle}>What players can expect</Typography>
            <Box component="ul" sx={sx.bulletList}>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Verification exists to protect the quality of the player pool.
                </Typography>
              </li>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Moderation and review back up the platform's trust systems.
                </Typography>
              </li>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Reputation and reporting are meant to reinforce one another.
                </Typography>
              </li>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Cleaner matches come from stronger standards, not only better branding.
                </Typography>
              </li>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 2 }}>
              <Button
                component={Link}
                to="/anti-cheat"
                variant="contained"
                sx={sx.primaryButton}
              >
                See anti-cheat
              </Button>
              <Button
                component={Link}
                to="/reputation"
                variant="outlined"
                sx={sx.secondaryButton}
              >
                Learn about reputation
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </PublicPageLayout>
  );
}
